import { Map } from 'immutable';
import { companyUnreadCounterSelector } from '@/domain/chat/facadeSelectors';

// eslint-disable-next-line no-restricted-syntax
import { loadedDataCompanyIdSelector } from '@/domain/documents/documentsLeafSelectors';
import { selector } from '@/lib/selectors';
import {
  isDokkaSupportSelector,
  userGUIDSelector,
  workSpaceTypeSelector,
  organizationIdSelector,
} from '@/domain/env/envSelector';
import { companyRoleSelector } from '@/domain/env/sessionRoleSelectors';

import { companiesFilter, companiesSort, checkPermissions, CompanyFactory } from '@/domain/companies/helpers';
import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';
import { companyFeatureFlagsSelector, configurationCompanySelector } from '@/domain/companies/companiesLeafSelectors';

import { AMOUNT_REPRESENTATION_VALUES } from '@/lib/amountRepresentationValues';

const companies = (state) => state.companies;

export const currentOrganizationId = (state) => state.organization.currentOrganization;
export const searchTermSelector = selector(companies, (c) => c.filter);
export const searchTextSelector = selector(companies, (c) => c.searchText);
export const searchTypeSelector = selector(companies, (c) => c.searchType);
export const searchSortSelector = selector(companies, (c) => c.sort);

export const refreshCompanySelector = selector(companies, (c) => c.refreshCompany);
export { configurationCompanySelector };
export const configurationCompanyIdSelector = selector(configurationCompanySelector, (c) => c.id);
export const shouldHighlightAmountSelector = selector(
  configurationCompanySelector,
  (c) => c.amounts_representation === AMOUNT_REPRESENTATION_VALUES.HIGHLIGHTED,
);
export const companySigningsSelector = selector(configurationCompanySelector, (c) => c.signings);
export const companyApplicationsSelector = selector(configurationCompanySelector, (c) => c.applications);

export const companySigningsCompanyIdSelector = selector(configurationCompanySelector, (c) => c.signingsCompanyId);

export const companyApprovalsFeatureEnabledSelector = selector(companyFeatureFlagsSelector, (f) => f.get('approvals'));

export const companyPaidUnpaidFeatureEnabledSelector = selector(companyFeatureFlagsSelector, (f) =>
  f.get('paid_unpaid'),
);
export const twoWayMatchingFeatureValueSelector = selector(companyFeatureFlagsSelector, (f) =>
  f.get('two_ways_matching'),
);

export const companiesByIdSelector = selector(companies, (c) => c.list);

export const isCompanyListLoadedSelector = selector(companies, (c) => c.isListLoaded);
export const isCompanyConfigurationLoadedSelector = selector(companies, (c) => c.isConfigurationLoaded);

export const getCurrentCompanyData = selector(
  companiesByIdSelector,
  currentCompanyIdSelector,
  (allCompanies, currentCompanyId) => allCompanies.get(currentCompanyId),
);

export const currentCompanyNameSelector = selector(companiesByIdSelector, currentCompanyIdSelector, (list, id) =>
  list.get(id, CompanyFactory()).get('cname'),
);

// The name of the company whose data the store holds, for the support chat, which sends it
// together with that company's ERP. Not the current company: see loadedDataCompanyIdSelector.
export const loadedDataCompanyNameSelector = selector(companiesByIdSelector, loadedDataCompanyIdSelector, (list, id) =>
  list.get(id, CompanyFactory()).get('cname'),
);

export const currentCompanyDateFormatSelector = selector(companiesByIdSelector, currentCompanyIdSelector, (list, id) =>
  list.get(id, CompanyFactory()).get('dateFormat'),
);

export const companyNotificationsSelector = companyUnreadCounterSelector;

export const organizationNotificationsSelector = selector(
  companyNotificationsSelector,
  companiesByIdSelector,
  (notifications, companyList) =>
    notifications.reduce((a, counter, companyId) => {
      const { organizationId } = companyList.get(companyId, {});
      if (typeof organizationId === 'undefined') {
        return a;
      }
      return a.update(organizationId, (u) => (u || 0) + counter);
    }, new Map()),
);

export const companiesListSelector = selector(
  companiesByIdSelector,
  searchSortSelector,
  companyNotificationsSelector,
  companiesSort,
);

export const companiesSelector = selector(
  companiesListSelector,
  searchTermSelector,
  isDokkaSupportSelector,
  companiesFilter,
);

export const companiesByOrganizationSelector = selector(
  companiesSelector,
  currentOrganizationId,
  (companyList, currentOrganization) =>
    currentOrganization === null
      ? companyList
      : companyList.filter((company) => company.organizationId === currentOrganization),
);

export const allCompaniesByOrganizationSelector = selector(
  companiesListSelector,
  currentOrganizationId,
  (companyList, currentOrganization) =>
    currentOrganization === null
      ? companyList
      : companyList.filter((company) => company.organizationId === currentOrganization),
);

// TODO: move to the CASL context of the document and workspace cases and remove this selector.
export const doesCurrentCompanySupportGeneralDocsOnlySelector = selector(
  companyFeatureFlagsSelector,
  (flags) => !flags.get('fin', false),
);

export const isSignAvailableSelector = selector(
  userGUIDSelector,
  companyRoleSelector,
  currentCompanyIdSelector,
  companiesByIdSelector,
  (id, role, companyId, list) => {
    const sp = list.getIn([companyId, 'signParams']);
    return Map({
      token1: checkPermissions(sp.getIn(['token1', 'permissions']), id, role),
      token2: checkPermissions(sp.getIn(['token2', 'permissions']), id, role),
    });
  },
);

export const documentWorkSpaceTypeSelector = selector(
  workSpaceTypeSelector,
  currentCompanyIdSelector,
  (ws, companyId) => ws.get(companyId) || 'tile',
);

export const isGridWorkSpaceSelector = selector(documentWorkSpaceTypeSelector, (wsType) => wsType === 'grid');

export const companiesWorkSpaceSelector = selector(companies, (c) => c.companiesWorkSpace);

export const companiesWorkSpaceTypeSelector = selector(
  companiesWorkSpaceSelector,
  organizationIdSelector,
  (ws, orgId) => ws.get(orgId) || 'tile',
);
